import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { fuzzyMatch } from "@/lib/fuzzy";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import {
    House, Plus, PencilSimple, Power, Trash, ArrowsClockwise, StopCircle,
    CurrencyInr, Calendar, Warning, CheckCircle, Clock,
    MagnifyingGlass, CaretDown, X, Receipt,
} from "@phosphor-icons/react";

const CATEGORIES = [
    { value: "rent",         label: "Rent",         color: "#f59e0b" },
    { value: "interest",     label: "Loan interest", color: "#ef4444" },
    { value: "insurance",    label: "Insurance",    color: "#8b5cf6" },
    { value: "subscription", label: "Subscription", color: "#0891b2" },
    { value: "utility",      label: "Utility",      color: "#10b981" },
    { value: "other",        label: "Other",        color: "#64748b" },
];

const STATUS_CHIPS = {
    paid:      { label: "Paid",       bg: "#dcfce7", fg: "#166534" },
    partial:   { label: "Partial",    bg: "#fef3c7", fg: "#92400e" },
    unpaid:    { label: "Overdue",    bg: "#fee2e2", fg: "#991b1b" },
    due:       { label: "Due — accrue", bg: "#e0e7ff", fg: "#3730a3" },
    overpaid:  { label: "Overpaid",   bg: "#fce7f3", fg: "#9d174d" },
};

/* ---------------------------- Searchable picker ---------------------------- */
// Inline, self-contained combobox — works inside a Dialog without portal / z-index
// tangles. Groups is [{label, items:[{id,name,group_name,tds_party_id?}]}].
function SearchablePicker({ value, onChange, groups, placeholder, testid }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const wrap = useRef(null);

    const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
    const selected = useMemo(() => flat.find((it) => it.id === value) || null, [flat, value]);

    const filtered = useMemo(() => {
        const needle = q.trim();
        if (!needle) return groups;
        return groups
            .map((g) => ({
                ...g,
                items: g.items.filter((it) =>
                    fuzzyMatch(needle, it.name) ||
                    fuzzyMatch(needle, it.group_name)
                ),
            }))
            .filter((g) => g.items.length > 0);
    }, [q, groups]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open]);

    return (
        <div ref={wrap} className="relative">
            <button type="button" onClick={() => setOpen((o) => !o)}
                    className="w-full border rounded-sm px-2 py-1.5 text-sm h-9 flex items-center justify-between bg-white"
                    style={{ borderColor: "var(--se-border)" }}
                    data-testid={testid}>
                <span className={selected ? "text-stone-800 truncate" : "text-stone-400"}>
                    {selected ? `${selected.group_name} · ${selected.name}` : placeholder}
                </span>
                <CaretDown size={12} className="text-stone-400 shrink-0" />
            </button>
            {open ? (
                <div className="absolute z-50 mt-1 left-0 right-0 border rounded-md bg-white shadow-lg"
                     style={{ borderColor: "var(--se-border)" }}>
                    <div className="p-2 border-b flex items-center gap-2" style={{ borderColor: "var(--se-border)" }}>
                        <MagnifyingGlass size={13} className="text-stone-400" />
                        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                               placeholder="Search expense heads…"
                               className="flex-1 text-sm outline-none"
                               data-testid={`${testid}-search`} />
                        {q ? (
                            <button type="button" onClick={() => setQ("")} className="text-stone-400 hover:text-stone-700">
                                <X size={13} />
                            </button>
                        ) : null}
                    </div>
                    <div className="max-h-64 overflow-y-auto" data-testid={`${testid}-list`}>
                        {filtered.length === 0 ? (
                            <div className="p-4 text-center text-xs text-stone-500">
                                No expense head matches “{q}”
                            </div>
                        ) : filtered.map((g) => (
                            <div key={g.label}>
                                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-stone-500 bg-stone-50">
                                    {g.label} ({g.items.length})
                                </div>
                                {g.items.map((it) => (
                                    <button type="button" key={it.id}
                                            onClick={() => { onChange(it.id); setOpen(false); setQ(""); }}
                                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-stone-100 ${it.id === value ? "bg-indigo-50" : ""}`}
                                            data-testid={`${testid}-opt-${it.id}`}>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] uppercase text-stone-500 shrink-0">{it.group_name}</span>
                                            <span className="truncate">{it.name}</span>
                                            {it.tds_party_id ? (
                                                <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 shrink-0">
                                                    TDS
                                                </span>
                                            ) : null}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default function RecurringExpenses() {
    const [rows, setRows] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // "new" object or existing recurring
    const [selected, setSelected] = useState(null); // recurring being drilled into

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [r, l, g] = await Promise.all([
                api.get("/recurring-expenses"),
                api.get("/ledgers"),
                api.get("/account-groups"),
            ]);
            setRows(r.data);
            setLedgers(l.data);
            setGroups(g.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const totalMonthly = useMemo(
        () => rows.filter((r) => r.active).reduce((s, r) => s + (r.amount || 0), 0),
        [rows]
    );
    const totalOutstanding = useMemo(
        () => rows.reduce((s, r) => s + (r.outstanding || 0), 0),
        [rows]
    );

    const toggle = async (rid, active) => {
        try {
            await api.post(`/recurring-expenses/${rid}/toggle-active?active=${active}`);
            toast.success(active ? "Reactivated" : "Deactivated");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (rid) => {
        if (!window.confirm("Delete this recurring expense? Only allowed if no accruals have been posted.")) return;
        try {
            await api.delete(`/recurring-expenses/${rid}`);
            toast.success("Deleted");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const stopRecurring = async (row) => {
        // Sets end_month so this recurring stops accruing after that month.
        // Anything already accrued (and any pending payment liability) stays put — you
        // still owe the vendor for the last month. Use "Delete" only if never accrued.
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const suggested = `${yyyy}-${mm}`;
        const picked = window.prompt(
            `Stop “${row.name}” after which month?\n` +
            "Format: YYYY-MM (e.g. 2026-08). Accruals will run through this month and then stop.\n" +
            "Any months already accrued but unpaid remain owed to the vendor.",
            row.end_month || suggested
        );
        if (!picked) return;
        if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(picked)) return toast.error("Use YYYY-MM format");
        if (picked < row.start_month) return toast.error(`End month can't be before start (${row.start_month})`);
        try {
            await api.patch(`/recurring-expenses/${row.id}`, { ...row, end_month: picked });
            toast.success(`“${row.name}” will stop after ${picked}`);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Recurring expenses"
                sub="Rent, interest, insurance and other fixed monthly costs. Accrued to P&L every month — whether or not paid — so the books tell the truth."
                right={
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="re-refresh">
                            <ArrowsClockwise size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
                        </Button>
                        <Button onClick={() => setEditing({ new: true })}
                                style={{ background: "var(--se-brand)", color: "#fff" }}
                                data-testid="re-new">
                            <Plus size={14} className="mr-2" /> Add recurring expense
                        </Button>
                    </div>
                } />

            <div className="p-8 space-y-4">
                {/* Summary strip */}
                <div className="grid grid-cols-3 gap-3">
                    <StatCard label="Monthly commitment" value={`₹ ${inr(totalMonthly)}`}
                              hint="Total of every active recurring expense" accent="#0f766e" />
                    <StatCard label="Currently outstanding" value={`₹ ${inr(totalOutstanding)}`}
                              hint={totalOutstanding > 0 ? "Unpaid across all months" : "Everything cleared"}
                              accent={totalOutstanding > 0 ? "#dc2626" : "#10b981"} />
                    <StatCard label="Active items" value={rows.filter((r) => r.active).length}
                              hint={`${rows.length} total`} accent="#6366f1" />
                </div>

                {/* Table */}
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    {rows.length === 0 ? (
                        <div className="p-16 text-center text-stone-500 text-sm">
                            <House size={32} className="mx-auto mb-3 text-stone-300" />
                            No recurring expenses defined. Click <b>Add recurring expense</b> to define your first — rent, loan interest, insurance, etc.
                        </div>
                    ) : (
                        <table className="se-table" data-testid="re-table">
                            <thead><tr>
                                <th>Name</th>
                                <th>Category</th>
                                <th>Expense head</th>
                                <th className="text-right">Monthly</th>
                                <th>Start → End</th>
                                <th className="text-right">Outstanding</th>
                                <th>Active</th>
                                <th className="text-right">Actions</th>
                            </tr></thead>
                            <tbody>
                                {rows.map((r) => {
                                    const cat = CATEGORIES.find((c) => c.value === r.category);
                                    return (
                                        <tr key={r.id} data-testid={`re-row-${r.id}`}>
                                            <td>
                                                <div className="font-medium">{r.name}</div>
                                                <div className="flex items-center gap-1 mt-0.5">
                                                    {r.vendor_name ? <span className="text-[10px] text-stone-500">{r.vendor_name}</span> : null}
                                                    {(r.billing_frequency && r.billing_frequency !== "monthly") ? (
                                                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 uppercase tracking-wider">
                                                            {r.billing_frequency}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="inline-block rounded px-2 py-0.5 text-[10px] font-medium"
                                                      style={{ background: `${cat?.color}20`, color: cat?.color }}>
                                                    {cat?.label}
                                                </span>
                                            </td>
                                            <td className="text-xs">{r.expense_ledger_name}</td>
                                            <td className="se-num text-right font-medium">₹ {inr(r.amount)}</td>
                                            <td className="text-xs">{r.start_month} → {r.end_month || "∞"}</td>
                                            <td className="se-num text-right font-medium"
                                                style={{ color: r.outstanding > 0 ? "#dc2626" : "#64748b" }}>
                                                ₹ {inr(r.outstanding || 0)}
                                            </td>
                                            <td>
                                                <span className={`se-chip ${r.active ? "se-chip-posted" : "se-chip-err"}`}>
                                                    {r.active ? "on" : "off"}
                                                </span>
                                            </td>
                                            <td className="text-right space-x-1 whitespace-nowrap">
                                                <Button size="sm" variant="outline" onClick={() => setSelected(r)}
                                                        data-testid={`re-view-${r.id}`}>
                                                    <Calendar size={13} className="mr-1" /> Schedule
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => setEditing(r)}
                                                        data-testid={`re-edit-${r.id}`}>
                                                    <PencilSimple size={13} />
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => toggle(r.id, !r.active)}
                                                        data-testid={`re-toggle-${r.id}`}>
                                                    <Power size={13} className={r.active ? "text-emerald-600" : "text-stone-400"} />
                                                </Button>
                                                {r.active && !r.end_month ? (
                                                    <Button size="sm" variant="ghost" onClick={() => stopRecurring(r)}
                                                            title="Stop this recurring (set an end month)"
                                                            data-testid={`re-stop-${r.id}`}>
                                                        <StopCircle size={13} className="text-orange-600" />
                                                    </Button>
                                                ) : null}
                                                <Button size="sm" variant="ghost" onClick={() => remove(r.id)}
                                                        className="hover:text-rose-600"
                                                        data-testid={`re-del-${r.id}`}>
                                                    <Trash size={13} />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {editing ? (
                <EditDialog initial={editing} ledgers={ledgers} groups={groups}
                            onClose={() => setEditing(null)}
                            onSaved={() => { setEditing(null); refresh(); }} />
            ) : null}
            {selected ? (
                <ScheduleDialog recurring={selected} ledgers={ledgers}
                                 onClose={() => setSelected(null)}
                                 onChanged={refresh} />
            ) : null}
        </>
    );
}

function StatCard({ label, value, hint, accent }) {
    return (
        <div className="bg-white border rounded-md p-4"
             style={{ borderColor: "var(--se-border)" }}>
            <div className="text-[11px] uppercase tracking-wider text-stone-500">{label}</div>
            <div className="text-2xl font-serif mt-1" style={{ color: accent }}>{value}</div>
            {hint ? <div className="text-[11px] text-stone-500 mt-1">{hint}</div> : null}
        </div>
    );
}

/* ---------------------------- Month picker ---------------------------- */
// Friendlier alternative to the native <input type="month">. Two side-by-side
// selects (month + year), can be "nullable" (e.g. for End month = "Runs forever").
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function MonthPicker({ value, onChange, nullable = false, nullLabel = "None", testid }) {
    // value is "YYYY-MM" or "" (null).
    const now = new Date();
    const [mm, yy] = value ? value.split("-") : ["", ""];
    const thisYear = now.getFullYear();
    // Year range: 3 years back to 6 years forward — covers most contracts.
    const years = Array.from({ length: 10 }, (_, i) => thisYear - 3 + i);

    const setMonth = (m) => onChange(yy ? `${yy}-${m.padStart(2, "0")}` : `${thisYear}-${m.padStart(2, "0")}`);
    const setYear = (y) => onChange(mm ? `${y}-${mm.padStart(2, "0")}` : `${y}-${String(now.getMonth() + 1).padStart(2, "0")}`);
    const clear = () => onChange("");

    const isEmpty = !value;
    return (
        <div className="flex gap-1">
            <select value={mm || ""} onChange={(e) => setMonth(e.target.value)}
                    className="flex-1 border rounded-sm px-2 py-1.5 text-sm h-9 bg-white"
                    style={{ borderColor: "var(--se-border)", color: isEmpty ? "#a8a29e" : undefined }}
                    data-testid={`${testid}-month`}>
                <option value="">{nullable ? nullLabel : "Month"}</option>
                {MONTH_NAMES.map((name, i) => (
                    <option key={i} value={String(i + 1).padStart(2, "0")}>{name}</option>
                ))}
            </select>
            <select value={yy || ""} onChange={(e) => setYear(e.target.value)}
                    className="w-[84px] border rounded-sm px-2 py-1.5 text-sm h-9 bg-white"
                    style={{ borderColor: "var(--se-border)", color: isEmpty ? "#a8a29e" : undefined }}
                    data-testid={`${testid}-year`}>
                <option value="">Year</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            {nullable && !isEmpty ? (
                <button type="button" onClick={clear}
                        className="text-[10px] text-stone-500 hover:text-stone-800 px-1"
                        title={nullLabel}
                        data-testid={`${testid}-clear`}>
                    ✕
                </button>
            ) : null}
        </div>
    );
}


/* ---------------------------- Edit dialog ---------------------------- */
function EditDialog({ initial, ledgers, groups, onClose, onSaved }) {
    const isNew = !!initial.new;
    const [f, setF] = useState({
        name: initial.name || "",
        expense_ledger_id: initial.expense_ledger_id || "",
        amount: initial.amount || "",
        start_month: initial.start_month || new Date().toISOString().slice(0, 7),
        end_month: initial.end_month || "",
        category: initial.category || "rent",
        vendor_name: initial.vendor_name || "",
        day_of_month: initial.day_of_month || 1,
        notes: initial.notes || "",
        billing_frequency: initial.billing_frequency || "monthly",
        gst_pct: initial.gst_pct != null ? initial.gst_pct : 18,
        gst_type: initial.gst_type || "none",
        tds_applicable: initial.tds_applicable || false,
        tds_section: initial.tds_section || "194I",
        tds_rate_pct: initial.tds_rate_pct != null ? initial.tds_rate_pct : 10,
        tds_party_type: initial.tds_party_type || "rent",
        tds_userset: initial.id ? true : false, // if editing an existing row, respect their choice
        rcm_applicable: initial.rcm_applicable || false,
    });
    const [busy, setBusy] = useState(false);
    const isPrepaid = f.billing_frequency !== "monthly";
    const monthsInCycle = f.billing_frequency === "annually" ? 12
                          : f.billing_frequency === "quarterly" ? 3 : 1;

    // TDS threshold suggestion — updates when category or amount change.
    // Sec 194-I (rent — land/building/furniture): 10%, threshold ₹6,00,000/year (FY25-26+).
    // Sec 194-A (interest ex-bank): 10%, threshold ₹5,000/year for non-bank / ₹40k for banks.
    // Sec 194-J (professional): 10%, threshold ₹30,000/year.
    // Sec 194-C (contractor): 1% ind / 2% others, threshold ₹30k single or ₹1L aggregate.
    const tdsSuggest = useMemo(() => {
        const monthly = Number(f.amount) || 0;
        const annual = monthly * 12;
        const cat = f.category;
        if (cat === "rent" && annual > 600000) {
            return { needed: true, section: "194I", rate: 10, party_type: "rent",
                     msg: `Annual rent ≈ ₹${inr(annual)} > ₹6,00,000 threshold (Sec 194-I). Deduct 10% TDS at each payment.` };
        }
        if (cat === "interest" && annual > 5000) {
            return { needed: true, section: "194A", rate: 10, party_type: "generic",
                     msg: `Annual interest ≈ ₹${inr(annual)} > ₹5,000 threshold (Sec 194-A). Deduct 10% TDS at each payment.` };
        }
        if (cat === "rent") {
            return { needed: false, section: "194I", rate: 10, party_type: "rent",
                     msg: `Annual rent ≈ ₹${inr(annual)} — below the ₹6,00,000 194-I threshold, no TDS needed.` };
        }
        if (cat === "interest") {
            return { needed: false, section: "194A", rate: 10, party_type: "generic",
                     msg: `Annual interest ≈ ₹${inr(annual)} — below ₹5,000 194-A threshold.` };
        }
        return { needed: false, section: "194J", rate: 10, party_type: "professional",
                 msg: `TDS logic depends on the vendor/section. Tick "Deduct TDS" and set the section manually if needed.` };
    }, [f.amount, f.category]);

    // Auto-adopt the suggestion on new rows (until the user manually toggles it).
    useEffect(() => {
        if (f.tds_userset) return;
        setF((cur) => ({
            ...cur,
            tds_applicable: tdsSuggest.needed,
            tds_section: tdsSuggest.section,
            tds_rate_pct: tdsSuggest.rate,
            tds_party_type: tdsSuggest.party_type,
        }));
    }, [tdsSuggest.needed, tdsSuggest.section, tdsSuggest.rate, tdsSuggest.party_type]);

    // Group P&L expense ledgers by category (Direct costs vs Indirect expenses).
    // The ledger doc carries `group_name` (e.g. "Rent"), but only `account_groups`
    // knows the category. We cross-reference by group_id/group_name.
    const { direct, indirect } = useMemo(() => {
        const catByGid = new Map();
        const catByName = new Map();
        (groups || []).forEach((g) => {
            catByGid.set(g.id, g.category);
            catByName.set(g.name, g.category);
        });
        const d = [], i = [];
        (ledgers || []).forEach((l) => {
            const cat = catByGid.get(l.group_id) || catByName.get(l.group_name);
            if (cat === "direct_cost") d.push(l);
            else if (cat === "indirect_expenses") i.push(l);
        });
        d.sort((a, b) => (a.group_name + a.name).localeCompare(b.group_name + b.name));
        i.sort((a, b) => (a.group_name + a.name).localeCompare(b.group_name + b.name));
        return { direct: d, indirect: i };
    }, [ledgers, groups]);

    const submit = async () => {
        if (!f.name.trim()) return toast.error("Name is required");
        if (!f.expense_ledger_id) return toast.error("Pick the expense head");
        if (!(Number(f.amount) > 0)) return toast.error("Amount must be greater than 0");
        if (f.gst_type !== "none" && !(Number(f.gst_pct) > 0)) {
            return toast.error("Set a GST % or switch GST type to None");
        }
        setBusy(true);
        try {
            const { tds_userset: _drop, ...rest } = f;
            const body = {
                ...rest,
                amount: Number(f.amount),
                day_of_month: Number(f.day_of_month) || 1,
                end_month: f.end_month || null,
                vendor_name: f.vendor_name.trim() || null,
                notes: f.notes.trim() || null,
                billing_frequency: f.billing_frequency,
                gst_pct: f.gst_type !== "none" ? (Number(f.gst_pct) || 0) : 0,
                gst_type: f.gst_type,
                tds_applicable: !!f.tds_applicable,
                tds_rate_pct: Number(f.tds_rate_pct) || 0,
                rcm_applicable: !!f.rcm_applicable,
            };
            if (isNew) await api.post("/recurring-expenses", body);
            else await api.patch(`/recurring-expenses/${initial.id}`, body);
            toast.success(isNew ? "Recurring expense created" : "Updated");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="re-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                        {isNew ? "New recurring expense" : `Edit ${initial.name}`}
                    </DialogTitle>
                </DialogHeader>
                {isNew ? (
                    <div className="text-[11px] rounded-sm px-3 py-2 border" style={{ background: "#fef3c7", borderColor: "#f59e0b", color: "#78350f" }} data-testid="re-non-gst-note">
                        <b>Use this for non-GST rents & subscriptions.</b> If your landlord (or vendor) gives you a proper GST bill, book it through <b>Purchase Invoices</b> with category = Rent instead — GST ITC flows correctly there.
                    </div>
                ) : null}
                <div className="space-y-3">
                    <div>
                        <Label>Name *</Label>
                        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                               placeholder="e.g. Office Rent - Chennai" data-testid="re-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Category</Label>
                            <select value={f.category}
                                    onChange={(e) => setF({ ...f, category: e.target.value })}
                                    className="w-full border rounded-sm px-2 py-1.5 text-sm h-9"
                                    style={{ borderColor: "var(--se-border)" }}
                                    data-testid="re-category">
                                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Monthly amount *</Label>
                            <Input type="number" step="0.01" value={f.amount}
                                   onChange={(e) => setF({ ...f, amount: e.target.value })}
                                   className="se-num" data-testid="re-amount" />
                        </div>
                    </div>
                    <div>
                        <Label>Expense head *</Label>
                        <SearchablePicker
                            value={f.expense_ledger_id}
                            onChange={(v) => setF({ ...f, expense_ledger_id: v })}
                            groups={[
                                { label: "Indirect expenses", items: indirect },
                                { label: "Direct costs",      items: direct },
                            ]}
                            placeholder="Pick expense head…"
                            testid="re-expense-head"
                        />
                        <div className="text-[10px] text-stone-500 mt-1">
                            {indirect.length + direct.length} expense ledger{indirect.length + direct.length === 1 ? "" : "s"} available
                            · Tip: pick the party-specific ledger (e.g. “Rent — Venkat Gowda”) if you have TDS to deduct at payment time
                        </div>
                    </div>

                    {/* Billing frequency (monthly vs prepaid quarterly / annually) */}
                    <div className="rounded-md border p-3 space-y-2"
                         style={{ borderColor: isPrepaid ? "#8b5cf6" : "var(--se-border)",
                                  background: isPrepaid ? "#faf5ff" : "#fafaf9" }}>
                        <Label className="text-[11px] uppercase tracking-wider">Billing cadence</Label>
                        <div className="flex gap-2">
                            {[
                                { v: "monthly", label: "Monthly", hint: "Pay each month, accrue liability" },
                                { v: "quarterly", label: "Quarterly (prepaid)", hint: "Pay upfront every 3 months" },
                                { v: "annually", label: "Annually (prepaid)", hint: "Pay upfront once a year" },
                            ].map((opt) => (
                                <button type="button" key={opt.v}
                                        onClick={() => setF({ ...f, billing_frequency: opt.v })}
                                        className={`flex-1 border rounded-md p-2 text-left text-xs ${f.billing_frequency === opt.v ? "border-indigo-500 bg-indigo-50" : "border-stone-200 bg-white hover:border-stone-300"}`}
                                        data-testid={`re-freq-${opt.v}`}>
                                    <div className="font-medium">{opt.label}</div>
                                    <div className="text-[10px] text-stone-500 mt-0.5">{opt.hint}</div>
                                </button>
                            ))}
                        </div>
                        {isPrepaid ? (
                            <div className="space-y-2 pt-2 border-t" style={{ borderColor: "var(--se-border)" }}>
                                <div className="text-[11px] text-stone-700">
                                    <b>Prepaid cycle:</b> Cycle = {monthsInCycle} months × ₹{inr(Number(f.amount) || 0)} = <b>₹{inr((Number(f.amount) || 0) * monthsInCycle)}</b> paid upfront.
                                    Each month, ₹{inr(Number(f.amount) || 0)} flushes from <i>Prepaid Expenses</i> to P&L.
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <Label className="text-[10px]">GST type</Label>
                                        <select value={f.gst_type}
                                                onChange={(e) => setF({ ...f, gst_type: e.target.value })}
                                                className="w-full border rounded-sm px-2 py-1 text-xs h-8 bg-white"
                                                style={{ borderColor: "var(--se-border)" }}
                                                data-testid="re-gst-type">
                                            <option value="none">No GST</option>
                                            <option value="intra">Intra (CGST+SGST)</option>
                                            <option value="inter">Inter-state (IGST)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-[10px]">GST %</Label>
                                        <select value={f.gst_pct}
                                                onChange={(e) => setF({ ...f, gst_pct: e.target.value })}
                                                disabled={f.gst_type === "none"}
                                                className="w-full border rounded-sm px-2 py-1 text-xs h-8 bg-white disabled:bg-stone-100"
                                                style={{ borderColor: "var(--se-border)" }}
                                                data-testid="re-gst-pct">
                                            {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-[10px]">Total paid</Label>
                                        <div className="border rounded-sm px-2 h-8 flex items-center text-xs se-num bg-white"
                                             style={{ borderColor: "var(--se-border)" }}
                                             data-testid="re-cycle-total">
                                            ₹ {inr(((Number(f.amount) || 0) * monthsInCycle) * (1 + (f.gst_type !== "none" ? (Number(f.gst_pct) || 0) / 100 : 0)))}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-[10px] text-stone-500">
                                    On payment, journals post: Dr Prepaid ₹{inr((Number(f.amount) || 0) * monthsInCycle)}
                                    {f.gst_type !== "none" && Number(f.gst_pct) > 0
                                        ? ` · Dr Input ${f.gst_type === "intra" ? "CGST+SGST" : "IGST"} ₹${inr(((Number(f.amount) || 0) * monthsInCycle) * Number(f.gst_pct) / 100)}`
                                        : ""}
                                    · Cr Bank
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2 pt-2 border-t" style={{ borderColor: "var(--se-border)" }}>
                                <div className="text-[11px] text-stone-700">
                                    <b>Monthly:</b> ₹{inr(Number(f.amount) || 0)} net + GST accrues each month
                                    {f.gst_type !== "none" && Number(f.gst_pct) > 0
                                        ? ` — total ₹${inr((Number(f.amount) || 0) * (1 + Number(f.gst_pct) / 100))}/mo`
                                        : ""}
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <Label className="text-[10px]">GST type</Label>
                                        <select value={f.gst_type}
                                                onChange={(e) => setF({ ...f, gst_type: e.target.value })}
                                                className="w-full border rounded-sm px-2 py-1 text-xs h-8 bg-white"
                                                style={{ borderColor: "var(--se-border)" }}
                                                data-testid="re-gst-type">
                                            <option value="none">No GST</option>
                                            <option value="intra">Intra (CGST+SGST)</option>
                                            <option value="inter">Inter-state (IGST)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-[10px]">GST %</Label>
                                        <select value={f.gst_pct}
                                                onChange={(e) => setF({ ...f, gst_pct: e.target.value })}
                                                disabled={f.gst_type === "none"}
                                                className="w-full border rounded-sm px-2 py-1 text-xs h-8 bg-white disabled:bg-stone-100"
                                                style={{ borderColor: "var(--se-border)" }}
                                                data-testid="re-gst-pct">
                                            {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-[10px]">Gross / mo</Label>
                                        <div className="border rounded-sm px-2 h-8 flex items-center text-xs se-num bg-white"
                                             style={{ borderColor: "var(--se-border)" }}
                                             data-testid="re-monthly-gross">
                                            ₹ {inr((Number(f.amount) || 0) * (1 + (f.gst_type !== "none" ? (Number(f.gst_pct) || 0) / 100 : 0)))}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-[10px] text-stone-500">
                                    Each accrual: Dr Expense ₹{inr(Number(f.amount) || 0)}
                                    {f.gst_type !== "none" && Number(f.gst_pct) > 0
                                        ? ` · Dr Input ${f.gst_type === "intra" ? "CGST+SGST" : "IGST"} ₹${inr((Number(f.amount) || 0) * Number(f.gst_pct) / 100)}`
                                        : ""}
                                    · Cr Payable
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>First month billed *</Label>
                            <MonthPicker value={f.start_month}
                                         onChange={(v) => setF({ ...f, start_month: v })}
                                         testid="re-start" />
                            <div className="text-[10px] text-stone-500 mt-1">
                                Accrual starts here — every 1st of this month, ₹{f.amount || "—"} hits P&L.
                            </div>
                        </div>
                        <div>
                            <Label>Last month billed</Label>
                            <MonthPicker value={f.end_month}
                                         onChange={(v) => setF({ ...f, end_month: v })}
                                         nullable
                                         nullLabel="Runs forever"
                                         testid="re-end" />
                            <div className="text-[10px] text-stone-500 mt-1">
                                Leave as “Runs forever” for rent/loans/subs. Set a month for a fixed-term contract.
                            </div>
                        </div>
                        <div className="col-span-2">
                            <Label>Due by day of month</Label>
                            <select value={f.day_of_month}
                                    onChange={(e) => setF({ ...f, day_of_month: e.target.value })}
                                    className="w-full sm:w-64 border rounded-sm px-2 py-1.5 text-sm h-9 bg-white"
                                    style={{ borderColor: "var(--se-border)" }}
                                    data-testid="re-day">
                                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                                    <option key={d} value={d}>
                                        {d}{d === 1 ? "st" : d === 2 ? "nd" : d === 3 ? "rd" : d > 20 && d % 10 === 1 ? "st" : d > 20 && d % 10 === 2 ? "nd" : d > 20 && d % 10 === 3 ? "rd" : "th"} of every month
                                    </option>
                                ))}
                            </select>
                            <div className="text-[10px] text-stone-500 mt-1">
                                After this day, unpaid months turn <b>overdue</b> in the schedule.
                            </div>
                        </div>
                    </div>

                    {/* TDS at source — auto-suggested from category + monthly amount */}
                    <div className="rounded-md border p-3 space-y-2" style={{ borderColor: tdsSuggest.needed ? "#f59e0b" : "var(--se-border)", background: tdsSuggest.needed ? "#fffbeb" : "#fafaf9" }}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="text-[11px] text-stone-700">
                                <b>TDS suggestion:</b> {tdsSuggest.msg}
                            </div>
                            {tdsSuggest.needed ? (
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 shrink-0 uppercase tracking-wider">
                                    Suggested ON
                                </span>
                            ) : null}
                        </div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={!!f.tds_applicable}
                                   onChange={(e) => setF({ ...f, tds_applicable: e.target.checked, tds_userset: true })}
                                   data-testid="re-tds-applicable" />
                            <span>Deduct TDS at every payment</span>
                        </label>
                        {f.tds_applicable ? (
                            <div className="grid grid-cols-3 gap-3 pt-1">
                                <div>
                                    <Label className="text-[10px]">Section</Label>
                                    <select value={f.tds_section}
                                            onChange={(e) => setF({ ...f, tds_section: e.target.value, tds_userset: true })}
                                            className="w-full border rounded-sm px-2 py-1 text-xs h-8 bg-white"
                                            style={{ borderColor: "var(--se-border)" }}
                                            data-testid="re-tds-section">
                                        <option value="194I">194-I · Rent (land/building)</option>
                                        <option value="194IB">194-IB · Rent by individual</option>
                                        <option value="194J">194-J · Professional</option>
                                        <option value="194A">194-A · Interest</option>
                                        <option value="194C">194-C · Contractor</option>
                                    </select>
                                </div>
                                <div>
                                    <Label className="text-[10px]">Rate %</Label>
                                    <Input type="number" step="0.1" value={f.tds_rate_pct}
                                           onChange={(e) => setF({ ...f, tds_rate_pct: e.target.value, tds_userset: true })}
                                           className="h-8 text-xs se-num"
                                           data-testid="re-tds-rate" />
                                </div>
                                <div>
                                    <Label className="text-[10px]">Per-month TDS</Label>
                                    <div className="border rounded-sm px-2 h-8 flex items-center text-xs se-num bg-white"
                                         style={{ borderColor: "var(--se-border)" }}
                                         data-testid="re-tds-preview">
                                        ₹ {inr(((Number(f.amount) || 0) * (Number(f.tds_rate_pct) || 0)) / 100)}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* RCM at source — for unregistered landlords / vendors giving NO GST bill */}
                    <div className="rounded-md border p-3 space-y-2" style={{ borderColor: f.rcm_applicable ? "#0369a1" : "var(--se-border)", background: f.rcm_applicable ? "#eff6ff" : "#fafaf9" }}>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={!!f.rcm_applicable}
                                   onChange={(e) => setF({ ...f, rcm_applicable: e.target.checked })}
                                   data-testid="re-rcm-applicable" />
                            <span><b>Does RCM apply?</b> (landlord/vendor is unregistered — no GST bill)</span>
                        </label>
                        {f.rcm_applicable ? (
                            <div className="text-[11px] text-blue-900 pt-1">
                                Rent recurring → <b>18% RCM</b> auto-booked every month · Other categories fall back to 5%.
                                Journal on each accrual will include <span className="font-mono bg-white px-1 py-0.5 rounded border" style={{ borderColor: "#93c5fd" }}>Dr RCM ITC · Cr RCM Payable</span>.
                                Toggle also shows in <b>Settings → Vendors with RCM</b> for later edits.
                            </div>
                        ) : null}
                    </div>
                    <div>
                        <Label>Vendor / label (for reference only)</Label>
                        <Input value={f.vendor_name} onChange={(e) => setF({ ...f, vendor_name: e.target.value })}
                               placeholder="e.g. Venkat Gowda (landlord), HDFC Home Loan A/C…"
                               data-testid="re-vendor" />
                        <div className="text-[10px] text-stone-500 mt-1">
                            Just a note that shows on the card — not a ledger link. Journals post to the “Expense head” you pick above.
                        </div>
                    </div>
                    <div>
                        <Label>Notes</Label>
                        <Textarea rows={2} value={f.notes}
                                  onChange={(e) => setF({ ...f, notes: e.target.value })}
                                  data-testid="re-notes" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy}
                            style={{ background: "var(--se-brand)", color: "#fff" }}
                            data-testid="re-save">
                        {busy ? "…" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ---------------------------- Schedule dialog ---------------------------- */
function ScheduleDialog({ recurring, ledgers, onClose, onChanged }) {
    const [periods, setPeriods] = useState([]);
    const [cycles, setCycles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [payTarget, setPayTarget] = useState(null);
    const [showPrepaid, setShowPrepaid] = useState(false);
    const [showBackfill, setShowBackfill] = useState(false);

    const isPrepaid = (recurring.billing_frequency || "monthly") !== "monthly";
    const monthsInCycle = recurring.billing_frequency === "annually" ? 12
                          : recurring.billing_frequency === "quarterly" ? 3 : 1;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const calls = [api.get(`/recurring-expenses/${recurring.id}/periods?months=18`)];
            if (isPrepaid) calls.push(api.get(`/recurring-expenses/${recurring.id}/prepaid-cycles`));
            const results = await Promise.all(calls);
            setPeriods(results[0].data);
            if (isPrepaid) setCycles(results[1].data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [recurring.id, isPrepaid]);
    useEffect(() => { load(); }, [load]);

    const accrue = async (month) => {
        try {
            await api.post(`/recurring-expenses/${recurring.id}/accrue-month?month=${month}`);
            toast.success(`${month} ${isPrepaid ? "amortised" : "accrued"} — journal posted`);
            load(); onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const reverseCycle = async (cid) => {
        if (!window.confirm("Reverse this prepaid cycle? Every monthly amortisation booked against it will also be reversed.")) return;
        try {
            const { data } = await api.delete(`/recurring-expenses/${recurring.id}/prepaid-cycles/${cid}`);
            toast.success(`Cycle reversed · ${data.reversed_amortisations} amortisation(s) undone`);
            load(); onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const totalOutstanding = useMemo(
        () => periods.reduce((s, p) => s + (p.outstanding || 0), 0),
        [periods]
    );

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="re-schedule-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl flex items-center gap-3 flex-wrap">
                        {recurring.name}
                        <span className="text-sm text-stone-500 font-sans">
                            · ₹ {inr(recurring.amount)}/mo · {recurring.start_month} → {recurring.end_month || "∞"}
                        </span>
                        {isPrepaid ? (
                            <span className="text-[10px] px-2 py-0.5 rounded bg-purple-100 text-purple-800 uppercase tracking-wider font-sans font-medium">
                                {recurring.billing_frequency} · prepaid
                            </span>
                        ) : null}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {/* Prepaid cycles panel */}
                    {isPrepaid ? (
                        <div className="rounded-md border p-3 space-y-2"
                             style={{ borderColor: "#c4b5fd", background: "#faf5ff" }}>
                            <div className="flex items-center justify-between">
                                <div className="text-sm">
                                    <b>Prepaid cycles</b> · Unamortised prepaid asset:
                                    <span className="ml-1 font-mono" data-testid="re-prepaid-balance">
                                        ₹ {inr(recurring.prepaid_balance || 0)}
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="outline"
                                            onClick={() => setShowBackfill(true)}
                                            data-testid="re-backfill-btn">
                                        <Clock size={12} className="mr-1" /> Backfill months
                                    </Button>
                                    <Button size="sm"
                                            onClick={() => setShowPrepaid(true)}
                                            style={{ background: "#7c3aed", color: "#fff" }}
                                            data-testid="re-record-cycle-btn">
                                        <Plus size={12} className="mr-1" /> Record prepaid cycle
                                    </Button>
                                </div>
                            </div>
                            {cycles.length === 0 ? (
                                <div className="text-xs text-stone-500 pt-1">
                                    No cycles recorded yet. Click <b>Record prepaid cycle</b> when you make the upfront payment (single Dr Prepaid / Cr Bank + Input GST journal).
                                </div>
                            ) : (
                                <table className="se-table" data-testid="re-cycles-table">
                                    <thead><tr>
                                        <th>Cycle</th>
                                        <th className="text-right">Net</th>
                                        <th className="text-right">GST</th>
                                        <th className="text-right">Total paid</th>
                                        <th>Paid on</th>
                                        <th>Ref</th>
                                        <th className="text-right">Actions</th>
                                    </tr></thead>
                                    <tbody>
                                        {cycles.map((c) => (
                                            <tr key={c.id} data-testid={`re-cycle-${c.id}`}
                                                className={c.status === "reversed" ? "opacity-50 line-through" : ""}>
                                                <td className="font-mono text-xs">{c.cycle_start_month} → {c.cycle_end_month}</td>
                                                <td className="se-num text-right">₹ {inr(c.cycle_net)}</td>
                                                <td className="se-num text-right text-stone-500">₹ {inr(c.gst_amount)}</td>
                                                <td className="se-num text-right font-medium">₹ {inr(c.total_paid)}</td>
                                                <td className="text-xs">{c.date}</td>
                                                <td className="text-[10px] text-stone-500">{c.ref_no || "—"}</td>
                                                <td className="text-right">
                                                    {c.status !== "reversed" ? (
                                                        <Button size="sm" variant="ghost"
                                                                onClick={() => reverseCycle(c.id)}
                                                                className="hover:text-rose-600"
                                                                data-testid={`re-cycle-reverse-${c.id}`}>
                                                            <Trash size={12} />
                                                        </Button>
                                                    ) : (
                                                        <span className="text-[10px] text-stone-400">reversed</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ) : null}

                    <div className="rounded-md p-3 flex items-center justify-between"
                         style={{ background: totalOutstanding > 0 ? "#fef2f2" : "#f0fdf4",
                                  border: `1px solid ${totalOutstanding > 0 ? "#fecaca" : "#bbf7d0"}` }}>
                        <div className="text-sm">
                            <b>Total outstanding for {recurring.name}: </b>
                            <span style={{ color: totalOutstanding > 0 ? "#991b1b" : "#166534" }}>
                                ₹ {inr(totalOutstanding)}
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            {totalOutstanding > 0 ? (
                                <span className="text-xs text-rose-600 flex items-center gap-1">
                                    <Warning size={12} weight="fill" /> Payments due
                                </span>
                            ) : (
                                <span className="text-xs text-emerald-600 flex items-center gap-1">
                                    <CheckCircle size={12} weight="fill" /> All clear
                                </span>
                            )}
                            {!isPrepaid ? (
                                <Button size="sm" variant="outline"
                                        onClick={() => setShowBackfill(true)}
                                        data-testid="re-backfill-btn">
                                    <Clock size={12} className="mr-1" /> Backfill months
                                </Button>
                            ) : null}
                        </div>
                    </div>

                    {loading ? (
                        <div className="p-8 text-center text-stone-400 text-sm">Loading…</div>
                    ) : (
                        <table className="se-table" data-testid="re-schedule-table">
                            <thead><tr>
                                <th>Month</th>
                                <th className="text-right">{isPrepaid ? "Amortised" : "Accrued"}</th>
                                <th className="text-right">Paid</th>
                                <th className="text-right">{isPrepaid ? "Prepaid" : "Outstanding"}</th>
                                <th>Status</th>
                                <th>Payments</th>
                                <th className="text-right">Actions</th>
                            </tr></thead>
                            <tbody>
                                {periods.map((p) => {
                                    const chipMap = {
                                        ...STATUS_CHIPS,
                                        amortised: { label: "Amortised", bg: "#f3e8ff", fg: "#6b21a8" },
                                        needs_prepaid: { label: "Needs prepaid", bg: "#fef3c7", fg: "#92400e" },
                                    };
                                    const chip = chipMap[p.status] || chipMap.due;
                                    return (
                                        <tr key={p.month} data-testid={`re-period-${p.month}`}>
                                            <td className="font-mono text-xs">{p.month}</td>
                                            <td className="se-num text-right">₹ {inr(p.accrued_amount || 0)}</td>
                                            <td className="se-num text-right">₹ {inr(p.paid_amount || 0)}</td>
                                            <td className="se-num text-right font-medium"
                                                style={{ color: p.outstanding > 0 ? "#dc2626" : "#64748b" }}>
                                                ₹ {inr(p.outstanding || 0)}
                                            </td>
                                            <td>
                                                <span className="inline-block rounded px-2 py-0.5 text-[10px] font-medium"
                                                      style={{ background: chip.bg, color: chip.fg }}>
                                                    {chip.label}
                                                </span>
                                            </td>
                                            <td className="text-[10px] text-stone-500">
                                                {isPrepaid && p.status === "amortised" ? (
                                                    <span className="text-purple-700">Paid upfront (cycle)</span>
                                                ) : (p.payments || []).length > 0 ? (
                                                    (p.payments || []).map((pay) => (
                                                        <div key={pay.id} title={pay.notes || ""}>
                                                            {pay.date} · ₹{inr(pay.amount)} · {pay.source_ledger_name}
                                                        </div>
                                                    ))
                                                ) : "—"}
                                            </td>
                                            <td className="text-right">
                                                {p.status === "due" && (!isPrepaid || p.prepaid_cycle_paid) ? (
                                                    <Button size="sm" onClick={() => accrue(p.month)}
                                                            style={{ background: "var(--se-brand)", color: "#fff" }}
                                                            data-testid={`re-accrue-${p.month}`}>
                                                        <Clock size={12} className="mr-1" /> {isPrepaid ? "Amortise" : "Accrue"}
                                                    </Button>
                                                ) : p.status === "needs_prepaid" ? (
                                                    <span className="text-[10px] text-amber-700">Record cycle first</span>
                                                ) : (!isPrepaid && p.outstanding > 0) ? (
                                                    <Button size="sm" onClick={() => setPayTarget(p)}
                                                            style={{ background: "#0f766e", color: "#fff" }}
                                                            data-testid={`re-pay-${p.month}`}>
                                                        <CurrencyInr size={12} className="mr-1" /> Record payment
                                                    </Button>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                {payTarget ? (
                    <PaymentDialog recurring={recurring} period={payTarget} ledgers={ledgers}
                                    onClose={() => setPayTarget(null)}
                                    onDone={() => { setPayTarget(null); load(); onChanged && onChanged(); }} />
                ) : null}
                {showPrepaid ? (
                    <PrepaidCycleDialog recurring={recurring} ledgers={ledgers}
                                         monthsInCycle={monthsInCycle}
                                         existingCycles={cycles}
                                         onClose={() => setShowPrepaid(false)}
                                         onDone={() => { setShowPrepaid(false); load(); onChanged && onChanged(); }} />
                ) : null}
                {showBackfill ? (
                    <BackfillDialog recurring={recurring}
                                     onClose={() => setShowBackfill(false)}
                                     onDone={() => { setShowBackfill(false); load(); onChanged && onChanged(); }} />
                ) : null}
            </DialogContent>
        </Dialog>
    );
}

/* ---------------------------- Payment dialog ---------------------------- */
function PaymentDialog({ recurring, period, ledgers, onClose, onDone }) {
    const tds = recurring.tds || null;             // { section, rate_pct, party_name, pan, ... }
    const gross = period.outstanding || 0;         // net + GST that the payable owes
    // TDS is always deducted on the NET (pre-GST) figure, per Income Tax rules.
    const tdsBase = period.accrued_net != null ? Number(period.accrued_net) : gross;
    const defaultTds = tds ? +(tdsBase * (tds.rate_pct || 0) / 100).toFixed(2) : 0;

    const [amount, setAmount] = useState(gross);   // GROSS satisfied against accrual
    const [tdsAmount, setTdsAmount] = useState(defaultTds);
    const [deductTds, setDeductTds] = useState(!!tds);
    const [source, setSource] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [refNo, setRefNo] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    // Keep TDS auto-linked to the NET portion of what's being paid (not on GST).
    const tdsTouched = useRef(false);
    useEffect(() => {
        if (!deductTds || !tds) { setTdsAmount(0); return; }
        if (!tdsTouched.current) {
            // Prorate: if user is paying a partial amount, deduct TDS on the
            // net share of that portion.
            const netShare = gross > 0 ? Number(amount) * (tdsBase / gross) : Number(amount);
            setTdsAmount(+(netShare * (tds.rate_pct || 0) / 100).toFixed(2));
        }
    }, [amount, deductTds, tds, tdsBase, gross]);

    const netToBank = Math.max(0, +(Number(amount) - Number(tdsAmount || 0)).toFixed(2));

    const cashBank = useMemo(
        () => (ledgers || []).filter((l) => l.party_type === "bank" || l.party_type === "cash"),
        [ledgers]
    );

    const submit = async () => {
        if (!source) return toast.error("Pick a cash/bank source");
        if (!(Number(amount) > 0)) return toast.error("Amount must be > 0");
        if (deductTds && !(Number(tdsAmount) >= 0)) return toast.error("TDS amount is invalid");
        if (deductTds && Number(tdsAmount) >= Number(amount)) return toast.error("TDS cannot equal or exceed the gross amount");
        setBusy(true);
        try {
            await api.post(`/recurring-expenses/${recurring.id}/periods/${period.id}/pay`, {
                date,
                amount: Number(amount),
                tds_amount: deductTds ? Number(tdsAmount) : 0,
                source_ledger_id: source,
                ref_no: refNo || null,
                notes: notes || null,
            });
            const bankMsg = deductTds
                ? ` · Bank ₹${inr(netToBank)} · TDS ₹${inr(Number(tdsAmount))}`
                : "";
            toast.success(`Payment recorded — ₹${inr(Number(amount))} against ${period.month}${bankMsg}`);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="re-pay-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                        Record payment — {recurring.name}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="rounded-md p-3 text-sm"
                         style={{ background: "#fef3c7", border: "1px solid #fde68a" }}>
                        Applying to <b>{period.month}</b> · Outstanding <b>₹ {inr(period.outstanding)}</b>
                    </div>

                    {tds ? (
                        <div className="rounded-md p-3 text-xs flex items-start gap-2"
                             style={{ background: "#eef2ff", border: "1px solid #c7d2fe" }}>
                            <Receipt size={16} className="text-indigo-700 mt-0.5" />
                            <div>
                                <b>TDS applicable</b> — {tds.party_name} · {tds.section} @ {tds.rate_pct}%
                                {tds.pan ? ` · PAN ${tds.pan}` : ""}. Tick the TDS box below to auto-split the payment so
                                the ledger books stay clean.
                            </div>
                        </div>
                    ) : null}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Date paid</Label>
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                                   data-testid="re-pay-date" />
                        </div>
                        <div>
                            <Label>Gross amount * {tds ? <span className="text-[10px] text-stone-500">(satisfies the accrual)</span> : null}</Label>
                            <Input type="number" step="0.01" value={amount}
                                   onChange={(e) => { tdsTouched.current = false; setAmount(e.target.value); }}
                                   className="se-num" data-testid="re-pay-amount" />
                        </div>
                    </div>

                    {tds ? (
                        <div className="rounded-md border p-3 space-y-2"
                             style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" checked={deductTds}
                                       onChange={(e) => { tdsTouched.current = false; setDeductTds(e.target.checked); }}
                                       data-testid="re-pay-tds-toggle" />
                                <span>Deduct TDS at source ({tds.section} @ {tds.rate_pct}%)</span>
                            </label>
                            {deductTds ? (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>TDS amount</Label>
                                        <Input type="number" step="0.01" value={tdsAmount}
                                               onChange={(e) => { tdsTouched.current = true; setTdsAmount(e.target.value); }}
                                               className="se-num" data-testid="re-pay-tds-amount" />
                                    </div>
                                    <div>
                                        <Label>Bank net (auto)</Label>
                                        <div className="border rounded-sm px-2 py-1.5 text-sm h-9 flex items-center se-num bg-white"
                                             style={{ borderColor: "var(--se-border)" }}
                                             data-testid="re-pay-net">
                                            ₹ {inr(netToBank)}
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                            <div className="text-[10px] text-stone-500">
                                Journal will post: Dr Payable ₹{inr(Number(amount))} · Cr Bank ₹{inr(netToBank)}
                                {deductTds ? ` · Cr TDS Payable ₹${inr(Number(tdsAmount || 0))}` : ""}
                            </div>
                        </div>
                    ) : null}

                    <div>
                        <Label>Pay from *</Label>
                        <select value={source} onChange={(e) => setSource(e.target.value)}
                                className="w-full border rounded-sm px-2 py-1.5 text-sm h-9"
                                style={{ borderColor: "var(--se-border)" }}
                                data-testid="re-pay-source">
                            <option value="">Pick a cash / bank ledger…</option>
                            {cashBank.map((l) => (
                                <option key={l.id} value={l.id}>
                                    {l.name} {l.system ? "(instant)" : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Reference</Label>
                            <Input value={refNo} onChange={(e) => setRefNo(e.target.value)}
                                   placeholder="UPI ref, cheque no…" data-testid="re-pay-ref" />
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                                   data-testid="re-pay-notes" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !source}
                            style={{ background: "#0f766e", color: "#fff" }}
                            data-testid="re-pay-submit">
                        {busy ? "…" : "Record payment"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ---------------------------- Prepaid cycle dialog ---------------------------- */
function PrepaidCycleDialog({ recurring, ledgers, monthsInCycle, existingCycles, onClose, onDone }) {
    // Suggest the next uncovered cycle_start_month
    const suggestedStart = useMemo(() => {
        if (existingCycles.length === 0) return recurring.start_month;
        const last = existingCycles
            .filter((c) => c.status !== "reversed")
            .map((c) => c.cycle_end_month)
            .sort()
            .slice(-1)[0];
        if (!last) return recurring.start_month;
        // Return the next month after last
        const [y, m] = last.split("-").map(Number);
        const ny = m === 12 ? y + 1 : y;
        const nm = m === 12 ? 1 : m + 1;
        return `${ny}-${String(nm).padStart(2, "0")}`;
    }, [existingCycles, recurring.start_month]);

    const [cycleStart, setCycleStart] = useState(suggestedStart);
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [source, setSource] = useState("");
    const [refNo, setRefNo] = useState("");
    const [notes, setNotes] = useState("");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [documentId, setDocumentId] = useState(null);
    const [busy, setBusy] = useState(false);

    const cashBank = useMemo(
        () => (ledgers || []).filter((l) => l.party_type === "bank" || l.party_type === "cash"),
        [ledgers]
    );

    const monthlyAmt = Number(recurring.amount) || 0;
    const cycleNet = monthlyAmt * monthsInCycle;
    const gstPct = recurring.gst_type !== "none" ? (Number(recurring.gst_pct) || 0) : 0;
    const gstAmount = +((cycleNet * gstPct) / 100).toFixed(2);
    const total = +(cycleNet + gstAmount).toFixed(2);

    // Compute cycle end for UI preview
    const cycleEnd = useMemo(() => {
        if (!cycleStart) return "";
        let [y, m] = cycleStart.split("-").map(Number);
        for (let i = 0; i < monthsInCycle - 1; i++) {
            m += 1;
            if (m > 12) { m = 1; y += 1; }
        }
        return `${y}-${String(m).padStart(2, "0")}`;
    }, [cycleStart, monthsInCycle]);

    const uploadFile = async () => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/recurring-expenses/${recurring.id}/prepaid-cycles/upload`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setDocumentId(data.document_id);
            toast.success("Invoice uploaded");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setUploading(false); }
    };

    const submit = async () => {
        if (!source) return toast.error("Pick a bank / cash source");
        if (!cycleStart || !/^\d{4}-(0[1-9]|1[0-2])$/.test(cycleStart)) return toast.error("Pick a cycle start month");
        setBusy(true);
        try {
            await api.post(`/recurring-expenses/${recurring.id}/prepaid-cycles`, {
                cycle_start_month: cycleStart,
                date,
                source_ledger_id: source,
                ref_no: refNo || null,
                notes: notes || null,
                document_id: documentId,
            });
            toast.success(`Prepaid cycle recorded · ${cycleStart} → ${cycleEnd} · ₹${inr(total)}`);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="re-cycle-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                        Record prepaid cycle — {recurring.name}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="rounded-md p-3 text-sm"
                         style={{ background: "#faf5ff", border: "1px solid #d8b4fe" }}>
                        Cycle: <b>{monthsInCycle} months</b> · ₹{inr(monthlyAmt)}/mo × {monthsInCycle} = <b>₹{inr(cycleNet)}</b>
                        {gstPct > 0 ? <> · GST {gstPct}% ({recurring.gst_type}) = <b>₹{inr(gstAmount)}</b></> : null}
                        · Total paid: <b>₹{inr(total)}</b>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Cycle starts *</Label>
                            <MonthPicker value={cycleStart} onChange={setCycleStart} testid="re-cycle-start" />
                            <div className="text-[10px] text-stone-500 mt-1">
                                Cycle covers <b>{cycleStart || "—"} → {cycleEnd || "—"}</b>
                            </div>
                        </div>
                        <div>
                            <Label>Paid on *</Label>
                            <Input type="date" value={date}
                                   onChange={(e) => setDate(e.target.value)}
                                   data-testid="re-cycle-date" />
                        </div>
                    </div>

                    <div>
                        <Label>Pay from *</Label>
                        <select value={source} onChange={(e) => setSource(e.target.value)}
                                className="w-full border rounded-sm px-2 py-1.5 text-sm h-9"
                                style={{ borderColor: "var(--se-border)" }}
                                data-testid="re-cycle-source">
                            <option value="">Pick a cash / bank ledger…</option>
                            {cashBank.map((l) => (
                                <option key={l.id} value={l.id}>{l.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Reference</Label>
                            <Input value={refNo} onChange={(e) => setRefNo(e.target.value)}
                                   placeholder="Cheque / UPI / policy no…"
                                   data-testid="re-cycle-ref" />
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                                   data-testid="re-cycle-notes" />
                        </div>
                    </div>

                    <div>
                        <Label>Invoice PDF (optional)</Label>
                        <div className="flex gap-2 items-center">
                            <input type="file" accept="application/pdf,image/*"
                                   onChange={(e) => { setFile(e.target.files?.[0] || null); setDocumentId(null); }}
                                   className="text-xs flex-1"
                                   data-testid="re-cycle-file" />
                            {file && !documentId ? (
                                <Button size="sm" variant="outline" onClick={uploadFile} disabled={uploading}
                                        data-testid="re-cycle-upload">
                                    {uploading ? "Uploading…" : "Upload"}
                                </Button>
                            ) : null}
                            {documentId ? (
                                <span className="text-[10px] px-2 py-1 rounded bg-emerald-100 text-emerald-700">
                                    ✓ Attached
                                </span>
                            ) : null}
                        </div>
                        <div className="text-[10px] text-stone-500 mt-1">
                            Attach the annual insurance / rent-agreement PDF. Optional — journals post regardless.
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !source}
                            style={{ background: "#7c3aed", color: "#fff" }}
                            data-testid="re-cycle-submit">
                        {busy ? "…" : `Record ₹${inr(total)} payment`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ---------------------------- Backfill dialog ---------------------------- */
function BackfillDialog({ recurring, onClose, onDone }) {
    const [preview, setPreview] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [uptoMonth, setUptoMonth] = useState(new Date().toISOString().slice(0, 7));
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(
                `/recurring-expenses/${recurring.id}/backfill-preview?upto_month=${uptoMonth}`
            );
            setPreview(data);
            // Auto-select every eligible month
            setSelected(new Set(data.months.filter((m) => m.eligible).map((m) => m.month)));
        } catch (e) { toast.error(formatApiError(e)); }
    }, [recurring.id, uptoMonth]);
    useEffect(() => { load(); }, [load]);

    const toggle = (m) => {
        const next = new Set(selected);
        if (next.has(m)) next.delete(m); else next.add(m);
        setSelected(next);
    };

    const submit = async () => {
        const months = [...selected];
        if (months.length === 0) return toast.error("Select at least one month");
        setBusy(true);
        try {
            const { data } = await api.post(
                `/recurring-expenses/${recurring.id}/backfill`,
                { months },
            );
            toast.success(`Backfilled ${data.posted_count} month(s)${data.skipped_count ? ` · ${data.skipped_count} skipped` : ""}`);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="re-backfill-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                        Backfill missed months — {recurring.name}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="rounded-md p-3 text-xs"
                         style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
                        <b>Preview only</b> — nothing posts until you click <i>Backfill selected</i>. Tick the months you want to
                        accrue / amortise now. Un-tick any you&apos;d rather skip.
                    </div>

                    <div className="grid grid-cols-2 gap-3 items-end">
                        <div>
                            <Label className="text-[11px]">Fill up to</Label>
                            <MonthPicker value={uptoMonth} onChange={setUptoMonth} testid="re-backfill-upto" />
                        </div>
                        <div className="text-[11px] text-stone-500 pb-1">
                            {preview
                                ? `${preview.eligible_count} eligible · ${preview.blocked_count} blocked`
                                : "Loading…"}
                        </div>
                    </div>

                    <div className="max-h-72 overflow-y-auto border rounded-md"
                         style={{ borderColor: "var(--se-border)" }}>
                        {preview && preview.months.length === 0 ? (
                            <div className="p-6 text-center text-xs text-stone-500">
                                Nothing to backfill — every month up to {uptoMonth} is already accrued.
                            </div>
                        ) : (
                            <table className="se-table" data-testid="re-backfill-table">
                                <thead><tr>
                                    <th></th>
                                    <th>Month</th>
                                    <th className="text-right">Amount</th>
                                    <th>Status</th>
                                </tr></thead>
                                <tbody>
                                    {(preview?.months || []).map((m) => (
                                        <tr key={m.month} data-testid={`re-backfill-row-${m.month}`}>
                                            <td>
                                                <input type="checkbox"
                                                       checked={selected.has(m.month)}
                                                       disabled={!m.eligible}
                                                       onChange={() => toggle(m.month)}
                                                       data-testid={`re-backfill-check-${m.month}`} />
                                            </td>
                                            <td className="font-mono text-xs">{m.month}</td>
                                            <td className="se-num text-right">₹ {inr(m.amount)}</td>
                                            <td className="text-[10px]">
                                                {m.eligible ? (
                                                    <span className="text-emerald-700">Ready</span>
                                                ) : (
                                                    <span className="text-amber-700">{m.reason}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="text-[11px] text-stone-500">
                        {selected.size} month{selected.size === 1 ? "" : "s"} selected · Total ₹{inr(selected.size * (recurring.amount || 0))}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || selected.size === 0}
                            style={{ background: "var(--se-brand)", color: "#fff" }}
                            data-testid="re-backfill-submit">
                        {busy ? "Posting…" : `Backfill ${selected.size} month${selected.size === 1 ? "" : "s"}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
